import { Checkbox, FormControlLabel, FormGroup, FormLabel, MenuItem, TextField } from '@mui/material';
import { useState } from 'react';

import type {
  IntegrationLink,
  IntegrationLinkSettings,
  IntegrationPlatform,
  IntegrationScope,
} from '../../api/business/types';
import { INTEGRATION_PLATFORMS } from '../../api/shared';
import { FormDialog } from '../../components/FormDialog';
import { useT } from '../../i18n/LanguageContext';

const SCOPES: Array<IntegrationScope> = ['stock.read', 'stock.write'];

type LinkSettingsDialogProps = {
  /** null to link a new shop. */
  link: IntegrationLink | null;
  onClose: () => void;
  /** The platform is chosen once, when the link is created. */
  onSubmit: (settings: IntegrationLinkSettings, platform: IntegrationPlatform) => void;
  isBusy: boolean;
  error: unknown;
};

/** What a link may do and from where: its name, scopes and the addresses its shop calls from. */
export const LinkSettingsDialog = ({ link, onClose, onSubmit, isBusy, error }: LinkSettingsDialogProps) => {
  const t = useT();
  const [name, setName] = useState(link?.name ?? '');
  const [platform, setPlatform] = useState<IntegrationPlatform>(link?.platform ?? INTEGRATION_PLATFORMS[0]);
  const [scopes, setScopes] = useState<Array<IntegrationScope>>(link?.scopes ?? SCOPES);
  const [allowlist, setAllowlist] = useState((link?.ipAllowlist ?? []).join('\n'));
  const [problem, setProblem] = useState<string | null>(null);

  const submit = (): void => {
    if (name.trim() === '' || scopes.length === 0) {
      setProblem(t('linkSettings.incomplete'));

      return;
    }

    setProblem(null);
    onSubmit(
      {
        name: name.trim(),
        scopes,
        ipAllowlist: allowlist
          .split(/[\s,]+/)
          .map((entry) => entry.trim())
          .filter((entry) => entry !== ''),
      },
      platform,
    );
  };

  return (
    <FormDialog
      title={link === null ? t('linkSettings.createTitle') : t('linkSettings.editTitle')}
      submitLabel={link === null ? t('linkSettings.create') : t('common.save')}
      onClose={onClose}
      onSubmit={submit}
      isBusy={isBusy}
      error={error}
      problem={problem}
    >
      {link === null ? (
        <TextField
          select
          label={t('linkSettings.platform')}
          helperText={t('linkSettings.platformHint')}
          value={platform}
          onChange={(e) => setPlatform(e.target.value as IntegrationPlatform)}
        >
          {INTEGRATION_PLATFORMS.map((value) => (
            <MenuItem key={value} value={value}>
              {t(`platform.${value}`)}
            </MenuItem>
          ))}
        </TextField>
      ) : null}
      <TextField
        required
        autoFocus
        label={t('linkSettings.name')}
        helperText={t('linkSettings.nameHint')}
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <FormGroup>
        <FormLabel>{t('linkSettings.scopes')}</FormLabel>
        {SCOPES.map((scope) => (
          <FormControlLabel
            key={scope}
            control={
              <Checkbox
                checked={scopes.includes(scope)}
                onChange={(e) =>
                  setScopes(e.target.checked ? [...scopes, scope] : scopes.filter((one) => one !== scope))
                }
              />
            }
            label={t(`scope.${scope}` as const)}
          />
        ))}
      </FormGroup>
      <TextField
        multiline
        minRows={2}
        label={t('linkSettings.allowlist')}
        helperText={t('linkSettings.allowlistHint')}
        placeholder="203.0.113.7, 198.51.100.0/24"
        value={allowlist}
        onChange={(e) => setAllowlist(e.target.value)}
        InputProps={{ sx: { fontFamily: 'ui-monospace, monospace' } }}
      />
    </FormDialog>
  );
};
